import { useEffect, useMemo, useState } from 'react';
import { ENDPOINTS } from '../services/apiService';
import { useDataContext } from '../context/DataContext';
import { useLoadingStore, useOperatorsStore } from '../store/loadingStore';

function getUniqueIds(data: Record<string, number>): number[] {
  const uniqueIds = new Set<number>();

  for (const key in data) {
    if (data.hasOwnProperty(key)) {
      uniqueIds.add(data[key]);
    }
  }

  return Array.from(uniqueIds);
}

const getTotalData = (data: any, type: any) => {
  const populationTotal = data.population_ef || data.population_facility;
  const typesTotal = data.equipment_type || data.facility_type;

  const totalIndex = Object.entries(typesTotal).find((elem) => {
    if (elem[1] === type) {
      return elem[0];
    }
    return;
  });

  return totalIndex && totalIndex[0] && populationTotal[totalIndex[0]];
};

function transformBenchmarkData(data: any, type = 'equip') {
  let result: { [p: string]: any } = {};

  Object.values(data.identifier).forEach((id: any, index) => {
    if (!result[id.toString()]) {
      result[id.toString()] = [];
    }

    const typeName =
      type === 'equip' ? data.equipment_type[index] : data.facility_type[index];

    result[id.toString()].push({
      type: typeName,
      total: getTotalData(data, typeName),
      count:
        type === 'equip'
          ? data.total_equipment_year[index]
          : data.total_facility_year[index],
      pop:
        type === 'equip'
          ? data.annual_Pop_EF[index]
          : data.avg_emissions_per_facility[index],
    });
  });

  return result;
}

export const useBenchmarkLevel = ({
  period,
  time,
}: {
  period: string;
  time: string;
}) => {
  const yearsArray = useMemo(() => {
    if (period.includes('All')) {
      return ['2024'];
    } else {
      const periodParts = period.split(' ');
      return [periodParts[1]];
    }
  }, [period]);

  const quartersArray = useMemo(() => {
    if (period.includes('All')) {
      return ['1', '2', '3', '4'];
    } else {
      const periodParts = period.split(' ');
      return [periodParts[0].slice(1)];
    }
  }, [period]);

  const [leakerEquipResults, setLeakerEquipResults] = useState<any>([]);

  const [leakerFacilityResults, setLeakerFacilityResults] = useState<any>([]);

  const { fetchMultipleAndSetData } = useDataContext();
  const setLoading = useLoadingStore(
    (state: { setLoading: any }) => state.setLoading,
  );

  const setOperators = useOperatorsStore(
    (state: { setOperators: any }) => state.setOperators,
  );

  const setSelectedOperator = useOperatorsStore(
    (state: { setSelectedOperator: any }) => state.setSelectedOperator,
  );

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        const results = await fetchMultipleAndSetData([
          {
            type: ENDPOINTS.benchmark,
            params: {
              quarter: '',
              year: yearsArray.join(','),
            },
          },
        ]);

        if (results) {
          const benchmarkData = results.find(
            (r) => r.type === ENDPOINTS.benchmark + '#' + 0,
          )?.data;

          if (benchmarkData && Object.entries(benchmarkData).length > 0) {
            const operatorIDs = getUniqueIds(
              benchmarkData.equipment_pop.identifier,
            );
            setOperators(operatorIDs);
            setSelectedOperator(operatorIDs[0]);

            const updatedBenchmarkEquipData = transformBenchmarkData(
              benchmarkData.equipment_pop,
            );

            const updatedBenchmarkFacilityData = transformBenchmarkData(
              benchmarkData.facility_pop,
              'facility',
            );

            setLeakerEquipResults(updatedBenchmarkEquipData);
            setLeakerFacilityResults(updatedBenchmarkFacilityData);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
      } finally {
        setTimeout(() => setLoading(false), 3000);
      }
    };

    fetchData();
  }, [
    fetchMultipleAndSetData,
    period,
    time,
    quartersArray,
    yearsArray,
    setLoading,
    setOperators,
    setSelectedOperator,
  ]);

  return {
    leakerEquipResults,
    leakerFacilityResults,
  };
};
